import { uniqBy, uniqWith } from 'lodash';
import {
  CapabilitiesGroupType,
  CapabilitiesKeyType,
  LicenseTypes,
  ManagedUser,
  RoleType,
  UserMembership,
  RoleOption,
  TEACHER_CAPABILITIES,
  ADMINISTRATOR_CAPABILITIES,
  Capabilities,
  Role,
} from '@hnm/types';
import { getAuth } from '@hnm/session-context';
import { getCommonUserContext } from '@hmhco/common-user-context';

export const isOrganizationIndividual = (membership: UserMembership): boolean =>
  membership.accountType === LicenseTypes.Individual;

export const isOrganizationInstitution = (
  membership: UserMembership,
): boolean => membership.accountType === LicenseTypes.Institutional;

export const getOrganizationLabel = (
  membership: UserMembership,
  // ideally, this would not need to be an input parameter if we had a way of storing some translations inside core
  individualLabel: string,
): string =>
  isOrganizationIndividual(membership)
    ? individualLabel
    : membership.hnmInstitutionName || '';

// this function should be removed, it actually complicates logic everywhere it is used, getOrganizationLabel should be used instead
export const getUserOrganizations = (
  memberships: Array<UserMembership>,
  // ideally, this would not need to be an input parameter if we had a way of storing some translations inside core
  individualLabel: string,
): Array<{ groupId: number; name: string }> =>
  memberships
    .map(membership => ({
      groupId: membership.groupId,
      name: getOrganizationLabel(membership, individualLabel),
    }))
    .filter(m => m.name.length);

export const getUniqueUsers = (users: ManagedUser[]): ManagedUser[] => {
  return uniqWith(users, (left: ManagedUser, right: ManagedUser) => {
    return left.id === right.id;
  });
};

export const getManagedOrganizations = (
  memberships: Array<UserMembership>,
): Array<UserMembership> => {
  const uniqOrganization: Array<UserMembership> = [];
  memberships
    .filter(membership => membership.membershipRole === RoleType.Owner)
    .filter(membership =>
      ([LicenseTypes.Institutional, LicenseTypes.Individual] as (
        | LicenseTypes
        | undefined
      )[]).includes(membership.accountType),
    )
    .forEach(membership => {
      if (membership.accountType === LicenseTypes.Institutional) {
        uniqOrganization.push(membership);
      }
    });
  const indvMembership = memberships.find(
    membership =>
      membership.accountType === LicenseTypes.Individual &&
      membership.membershipRole === RoleType.Owner,
  );
  if (indvMembership) {
    uniqOrganization.push(indvMembership);
  }
  return uniqOrganization;
};

export const getDistinctOrganizations = (
  memberships: Array<UserMembership>,
): Array<UserMembership> => {
  return uniqBy(memberships, membership => membership.groupId);
};

export const isAbleToManageUser = (user): boolean => {
  return user.groupMemberships.some(groupMemberships => {
    return (
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.manageUser &&
          capability.value === 'allow',
      ) &&
      (groupMemberships.capabilitiesGroup?.type ===
        CapabilitiesGroupType.individual ||
        groupMemberships.capabilitiesGroup?.type ===
          CapabilitiesGroupType.institution)
    );
  });
};

export const isAbleToLicence = (role, user): boolean => {
  const hasLicensingCapability = user?.groupMemberships.some(
    groupMemberships => {
      return groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.manageLicense &&
          capability.value === 'allow',
      );
    },
  );
  return (
    role === Role.DistrictAdmin ||
    role === Role.SchoolAdmin ||
    (role === Role.Teacher && hasLicensingCapability)
  );
};

export const isAbleToRoster = (role): boolean => {
  return role === Role.DistrictAdmin || role === Role.SchoolAdmin;
};

export const isAbleToDiscover = (user): boolean => {
  return user.groupMemberships.some(groupMemberships => {
    return (
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.viewProduct &&
          capability.value === 'allow',
      ) &&
      groupMemberships.capabilitiesGroup?.type === CapabilitiesGroupType.private
    );
  });
};

export const isAbleToTakeAssessment = (role): boolean => {
  return role === Role.Student;
};

export const getDistinctOrganizationsPreferredAdmin = (
  memberships: Array<UserMembership>,
): Array<UserMembership> => {
  const sortedMemberships = [...memberships].sort(
    (membershipA, membershipB) => {
      if (membershipA.membershipRole === membershipB.membershipRole) {
        return 0;
      }
      if (membershipA.membershipRole === RoleType.Owner) {
        return -1;
      }
      return 1;
    },
  );
  return getDistinctOrganizations(sortedMemberships);
};

export const roleOptionToCapabilities = (
  roleOption: RoleOption | undefined,
): Capabilities => {
  return roleOption === RoleOption.Teacher || roleOption === undefined
    ? TEACHER_CAPABILITIES
    : ADMINISTRATOR_CAPABILITIES;
};

export const roleOptionToRole = (
  roleOption: RoleOption | undefined,
): RoleType => {
  return roleOption === RoleOption.Teacher || roleOption === undefined
    ? RoleType.Member
    : RoleType.Owner;
};

export const isDealer2User = (user): boolean => {
  return user.groupMemberships.some(groupMemberships => {
    return groupMemberships?.capabilitiesGroup?.linkedAccounts?.some(
      accounts => accounts.account.dealerId === 2,
    );
  });
};

export const isRoleDistrictAdmin = (user): boolean => {
  const auth = getAuth();
  if (Number(auth?.authContext?.idToken?.claims?.tenant_id) !== 2) {
    return (
      user.groupMemberships.some(groupMemberships => {
        return (
          groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.manageDistrict &&
              capability.value === 'allow',
          ) &&
          groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.worksForDistrict &&
              capability.value === 'allow',
          )
        );
      }) && isDealer2User(user)
    );
  }
  return user.groupMemberships.some(groupMemberships => {
    return (
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.manageDistrict &&
          capability.value === 'allow',
      ) &&
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.worksForDistrict &&
          capability.value === 'allow',
      ) &&
      groupMemberships.capabilitiesGroup?.type ===
        CapabilitiesGroupType.district &&
      groupMemberships.capabilitiesGroup.linkedAccounts?.some(
        accounts => accounts.account.dealerId === 2,
      )
    );
  });
};

export const isRoleSchoolAdmin = (user): boolean => {
  const auth = getAuth();
  if (Number(auth?.authContext?.idToken?.claims?.tenant_id) !== 2) {
    return (
      user.groupMemberships.some(groupMemberships => {
        return (
          groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.manageSchool &&
              capability.value === 'allow',
          ) &&
          groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.worksForSchool &&
              capability.value === 'allow',
          )
        );
      }) && isDealer2User(user)
    );
  }
  return user.groupMemberships.some(groupMemberships => {
    return (
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.manageSchool &&
          capability.value === 'allow',
      ) &&
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.worksForSchool &&
          capability.value === 'allow',
      ) &&
      groupMemberships.capabilitiesGroup?.type ===
        CapabilitiesGroupType.school &&
      groupMemberships.capabilitiesGroup.linkedAccounts?.some(
        accounts => accounts.account.dealerId === 2,
      )
    );
  });
};

export const isRoleTeacher = (user): boolean => {
  const auth = getAuth();
  if (Number(auth?.authContext?.idToken?.claims?.tenant_id) !== 2) {
    return (
      user.groupMemberships.some(groupMemberships => {
        return (
          (groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.manageClassroom &&
              capability.value === 'allow',
          ) &&
            groupMemberships.capabilities?.some(
              capability =>
                capability.key === CapabilitiesKeyType.worksForSchool &&
                capability.value === 'allow',
            )) ||
          groupMemberships.capabilities?.some(
            capability =>
              capability.key === CapabilitiesKeyType.teachClass &&
              capability.value === 'allow',
          )
        );
      }) && isDealer2User(user)
    );
  }
  return user.groupMemberships.some(groupMemberships => {
    return (
      (groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.manageClassroom &&
          capability.value === 'allow',
      ) &&
        groupMemberships.capabilities?.some(
          capability =>
            capability.key === CapabilitiesKeyType.worksForSchool &&
            capability.value === 'allow',
        ) &&
        groupMemberships.capabilitiesGroup?.type ===
          CapabilitiesGroupType.school &&
        groupMemberships.capabilitiesGroup.linkedAccounts?.some(
          accounts => accounts.account.dealerId === 2,
        )) ||
      groupMemberships.capabilities?.some(
        capability =>
          capability.key === CapabilitiesKeyType.teachClass &&
          capability.value === 'allow' &&
          groupMemberships.capabilitiesGroup?.type ===
            CapabilitiesGroupType.class &&
          groupMemberships.capabilitiesGroup.linkedAccounts?.some(
            accounts => accounts.account.dealerId === 2,
          ),
      )
    );
  });
};

export const isRoleStudent = (user): boolean => {
  const auth = getAuth();
  if (Number(auth?.authContext?.idToken?.claims?.tenant_id) !== 2) {
    return (
      user.groupMemberships.some(groupMemberships => {
        return groupMemberships.capabilities?.some(
          capability =>
            (capability.key === CapabilitiesKeyType.attendSchool &&
              capability.value === 'allow') ||
            (capability.key === CapabilitiesKeyType.attendClass &&
              capability.value === 'allow'),
        );
      }) && isDealer2User(user)
    );
  }
  return user.groupMemberships.some(groupMemberships => {
    return groupMemberships.capabilities?.some(
      capability =>
        (capability.key === CapabilitiesKeyType.attendSchool &&
          capability.value === 'allow' &&
          groupMemberships.capabilitiesGroup?.type ===
            CapabilitiesGroupType.school &&
          groupMemberships.capabilitiesGroup.linkedAccounts?.some(
            accounts => accounts.account.dealerId === 2,
          )) ||
        (capability.key === CapabilitiesKeyType.attendClass &&
          capability.value === 'allow' &&
          groupMemberships.capabilitiesGroup?.type ===
            CapabilitiesGroupType.class &&
          groupMemberships?.capabilitiesGroup.linkedAccounts?.some(
            accounts => accounts.account.dealerId === 2,
          )),
    );
  });
};

export const isAbleToReport = (role): boolean => {
  return (
    role === Role.DistrictAdmin ||
    role === Role.SchoolAdmin ||
    role === Role.Teacher
  );
};

export const isAbleToManageClass = (role): boolean => {
  return role === Role.Teacher;
};

export const discoverRole = (): string => {
  const {
    role,
    isSchoolAdmin,
    isDistrictAdmin,
    isLearner,
  } = getCommonUserContext();

  if (isDistrictAdmin) {
    return Role.DistrictAdmin;
  }
  if (isSchoolAdmin) {
    return Role.SchoolAdmin;
  }
  if (role === 'Instructor') {
    return Role.Teacher;
  }
  if (isLearner) {
    return Role.Student;
  }
  if (role === 'Mentor') {
    return Role.Parent;
  }
  return '';
};
